import { Checkbox } from '@/shared/ui/primitives'
import { DatePicker, FormInput } from '@/shared/ui/components'
import { Label } from '@/shared/ui/primitives'
import { LICENSE_TYPE_LABELS, type LicenseType } from '@/shared/domain/license'
import { EMPTY_LICENSE_ENTRY, type LicenseEntry } from './form-values'

function formatLicenseTypeLabel(licenseType: string): string {
  return LICENSE_TYPE_LABELS[licenseType as LicenseType] ?? licenseType.replace(/_/g, ' ')
}

type LicenseCollectionSectionProps = {
  requiredLicenseTypes: string[]
  exemptionRules: Array<{ licenseType: string; descriptions: string[] }>
  licenseEntries: Record<string, LicenseEntry>
  errors: Record<string, string>
  isSubmitting: boolean
  onLicenseChange: (licenseType: string, field: keyof LicenseEntry, value: string | boolean) => void
}

export function LicenseCollectionSection({
  requiredLicenseTypes,
  exemptionRules,
  licenseEntries,
  errors,
  isSubmitting,
  onLicenseChange,
}: LicenseCollectionSectionProps) {
  return (
    <div className="flex flex-col gap-6">
      {requiredLicenseTypes.map((licenseType) => {
        const entry = licenseEntries[licenseType] ?? EMPTY_LICENSE_ENTRY
        const exemptionRule = exemptionRules.find((r) => r.licenseType === licenseType)
        const licenseNumberError = errors[`licenseEntries.${licenseType}.licenseNumber`]
        const licenseExpiryError = errors[`licenseEntries.${licenseType}.expiry`]

        return (
          <div key={licenseType} className="flex flex-col gap-3 rounded-lg border p-4">
            <h4 className="text-lg font-semibold">{formatLicenseTypeLabel(licenseType)}</h4>

            {exemptionRule ? (
              <div className="flex items-start gap-2">
                <Checkbox
                  id={`exempt-${licenseType}`}
                  checked={entry.exempt}
                  onCheckedChange={(checked) =>
                    onLicenseChange(licenseType, 'exempt', checked === true)
                  }
                  disabled={isSubmitting}
                />
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`exempt-${licenseType}`} className="text-sm font-medium">
                    I am exempt from this requirement
                  </Label>
                  {exemptionRule.descriptions.map((desc) => (
                    <p key={desc} className="text-xs text-muted-foreground">
                      {desc}
                    </p>
                  ))}
                </div>
              </div>
            ) : null}

            {entry.exempt ? (
              <p className="text-sm text-muted-foreground">
                You have declared an exemption for this license type.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormInput
                    id={`license-number-${licenseType}`}
                    label="License number"
                    required
                    value={entry.licenseNumber}
                    onChange={(v) => onLicenseChange(licenseType, 'licenseNumber', v)}
                    error={licenseNumberError}
                    disabled={isSubmitting}
                  />
                  <DatePicker
                    id={`license-expiry-${licenseType}`}
                    label="Expiration (optional)"
                    value={entry.expiry}
                    onChange={(v) => onLicenseChange(licenseType, 'expiry', v)}
                    error={licenseExpiryError}
                    disabled={isSubmitting}
                    clearable
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  You can upload an image or PDF of your license from the My Licenses page in the mobile app.
                </p>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
